import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDragToPanOptions {
	threshold?: number;
}

const INTERACTIVE_SELECTOR =
	'input, textarea, select, [contenteditable="true"]';

interface DragState {
	active: boolean;
	didPan: boolean;
	startX: number;
	startY: number;
	scrollLeft: number;
	scrollTop: number;
}

export const useDragToPan = <T extends HTMLElement = HTMLDivElement>(
	options: UseDragToPanOptions = {}
) => {
	const { threshold = 5 } = options;
	const containerRef = useRef<T>(null);
	const [isPanning, setIsPanning] = useState(false);
	const [canPan, setCanPan] = useState(false);

	const dragState = useRef<DragState>({
		active: false,
		didPan: false,
		startX: 0,
		startY: 0,
		scrollLeft: 0,
		scrollTop: 0,
	});

	const refreshCanPan = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const overflowing =
			container.scrollWidth > container.clientWidth ||
			container.scrollHeight > container.clientHeight;

		setCanPan((current) => (current === overflowing ? current : overflowing));
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		refreshCanPan();

		const handleMouseMove = (event: MouseEvent) => {
			const state = dragState.current;
			if (!state.active) return;

			const dx = event.clientX - state.startX;
			const dy = event.clientY - state.startY;

			if (!state.didPan) {
				if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
				state.didPan = true;
				document.body.style.userSelect = 'none';
				setIsPanning(true);
			}

			event.preventDefault();
			container.scrollLeft = state.scrollLeft - dx;
			container.scrollTop = state.scrollTop - dy;
		};

		const endDrag = () => {
			const state = dragState.current;
			if (!state.active) return;

			state.active = false;
			document.body.style.userSelect = '';
			setIsPanning(false);
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', endDrag);
		};

		const handleMouseDown = (event: MouseEvent) => {
			if (event.button !== 0) return;

			const target = event.target as HTMLElement | null;
			if (target?.closest(INTERACTIVE_SELECTOR)) return;

			refreshCanPan();
			const overflowing =
				container.scrollWidth > container.clientWidth ||
				container.scrollHeight > container.clientHeight;
			if (!overflowing) return;

			dragState.current = {
				active: true,
				didPan: false,
				startX: event.clientX,
				startY: event.clientY,
				scrollLeft: container.scrollLeft,
				scrollTop: container.scrollTop,
			};

			window.addEventListener('mousemove', handleMouseMove);
			window.addEventListener('mouseup', endDrag);
		};

		const handleClickCapture = (event: MouseEvent) => {
			if (dragState.current.didPan) {
				event.preventDefault();
				event.stopPropagation();
				dragState.current.didPan = false;
			}
		};

		const handleHoverMove = () => {
			if (!dragState.current.active) refreshCanPan();
		};

		container.addEventListener('mousedown', handleMouseDown);
		container.addEventListener('click', handleClickCapture, true);
		container.addEventListener('mouseenter', refreshCanPan);
		container.addEventListener('mousemove', handleHoverMove);
		window.addEventListener('resize', refreshCanPan);

		return () => {
			container.removeEventListener('mousedown', handleMouseDown);
			container.removeEventListener('click', handleClickCapture, true);
			container.removeEventListener('mouseenter', refreshCanPan);
			container.removeEventListener('mousemove', handleHoverMove);
			window.removeEventListener('resize', refreshCanPan);
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', endDrag);
			document.body.style.userSelect = '';
		};
	}, [refreshCanPan, threshold]);

	return { containerRef, isPanning, canPan };
};

export default useDragToPan;
